import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const processSteps = [
  { title: 'Prepare', desc: 'Guidance on repairs, updates and presentation to position your home for success.' },
  { title: 'Price', desc: 'A clear pricing strategy based on local market data and comparable property sales.' },
  { title: 'Market', desc: 'Professional marketing across multiple channels designed to attract serious buyers.' },
  { title: 'Show', desc: 'Managing viewings and buyer enquiries so you can focus on your next move.' },
  { title: 'Negotiate', desc: 'Skilled negotiation to help you secure the strongest possible result.' },
  { title: 'Close', desc: 'Support through inspections, appraisals and the final closing process.' },
];

const faqs = [
  { q: 'How do I know what my home is worth?', a: 'Carol will review recent comparable sales in your area, assess your property\'s condition and features, and provide you with a clear market analysis so you can make an informed decision.' },
  { q: 'How long does the selling process take?', a: 'Every situation is different, but from listing to closing typically takes 30–60 days. Carol will give you a realistic timeline based on your local market.' },
  { q: 'Do I need to make repairs before listing?', a: 'Not necessarily. Carol can advise on which improvements are likely to deliver a return and which you can skip. Sometimes minor updates make a big difference.' },
  { q: 'What does it cost to sell a home?', a: 'Costs typically include agent commission, closing fees, and any agreed-upon repairs or concessions. Carol will provide a clear estimate before you commit to anything.' },
  { q: 'Can I sell and buy at the same time?', a: 'Yes — many homeowners do. Carol can help you coordinate both transactions so the timing works for you.' },
];

export default function SellYourHome() {
  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <p className="text-primary-500 font-body text-sm tracking-widest uppercase mb-3">Sell Your Home</p>
          <h2 className="font-heading text-foreground-900 text-3xl md:text-4xl lg:text-5xl mb-5">
            Sell Your Home With Confidence
          </h2>
          <p className="text-foreground-600 font-body text-base md:text-lg max-w-[650px] mx-auto mb-8">
            Selling your home is a major decision. Carol Berry provides the knowledge, strategy and personal support
            you need to navigate the process and achieve the best possible result.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/home-valuation" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
              <i className="ri-home-4-line text-lg" />Request a Free Home Valuation
            </Link>
            <Link to="/contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-foreground-300 text-foreground-700 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-foreground-50 transition-all">
              <i className="ri-chat-3-line text-lg" />Talk to Carol
            </Link>
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-4">
              The Complete Selling Process
            </h2>
            <p className="text-foreground-600 font-body text-base max-w-[600px] mx-auto">
              From the first conversation to the closing table — here is how Carol guides you through every stage.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((s, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading text-sm font-bold mb-4">
                  {i + 1}
                </div>
                <h3 className="font-heading text-foreground-900 text-lg mb-2">{s.title}</h3>
                <p className="text-foreground-600 font-body text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Preparation */}
      <section className="py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="flex-1">
              <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-5">
                Preparing Your Property for the Market
              </h2>
              <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed mb-6">
                First impressions matter. Carol will walk through your property with you and provide practical,
                honest advice on how to present your home in its best light — without spending more than you need to.
              </p>
              <ul className="space-y-3">
                {['Decluttering and depersonalising', 'Minor repairs and touch-ups', 'Deep cleaning throughout', 'Curb appeal and exterior presentation', 'Optional professional staging'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-check-line text-primary-500 mt-0.5" />
                    <span className="text-foreground-700 font-body text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 w-full lg:w-[420px]">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=Beautifully%20staged%20modern%20living%20room%20with%20neutral%20tones%20natural%20light%20streaming%20through%20large%20windows%20comfortable%20furniture%20arrangement%20fresh%20flowers%20on%20coffee%20table%20clean%20minimalist%20aesthetic%20professional%20real%20estate%20interior%20photography&width=840&height=630&seq=sell-prep-01&orientation=landscape" alt="Beautifully staged living room ready for viewings" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Marketing */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h3 className="font-heading text-foreground-900 text-xl md:text-2xl mb-4">Pricing and Market Positioning</h3>
              <p className="text-foreground-600 font-body text-sm leading-relaxed mb-4">
                Pricing a home correctly is both art and science. Carol will analyse recent comparable sales,
                current market conditions and buyer demand in your area to recommend a price that attracts
                interest while protecting your interests.
              </p>
              <p className="text-foreground-600 font-body text-sm leading-relaxed">
                An overpriced home can sit on the market and lose momentum. An underpriced home can leave
                money on the table. Carol's goal is to help you find the right balance.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-foreground-900 text-xl md:text-2xl mb-4">Property Marketing</h3>
              <p className="text-foreground-600 font-body text-sm leading-relaxed mb-4">
                Your property deserves to be seen by the right buyers. Carol coordinates a professional
                marketing approach that includes high-quality photography, online listings, social media
                exposure and her professional network.
              </p>
              <p className="text-foreground-600 font-body text-sm leading-relaxed">
                The goal is simple: maximise visibility and generate genuine buyer interest so you have
                options when it is time to review offers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Managing Viewings & Offers */}
      <section className="py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h3 className="font-heading text-foreground-900 text-xl md:text-2xl mb-4">Managing Viewings and Buyer Enquiries</h3>
              <p className="text-foreground-600 font-body text-sm leading-relaxed">
                Carol handles all buyer enquiries and coordinates viewings so you don't have to. She screens
                potential buyers, gathers feedback after each showing and keeps you informed of how your
                property is being received in the market.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-foreground-900 text-xl md:text-2xl mb-4">Reviewing and Negotiating Offers</h3>
              <p className="text-foreground-600 font-body text-sm leading-relaxed">
                When offers come in, Carol will walk you through each one — not just the price, but the terms,
                contingencies and timeline. She will help you understand your options and negotiate to protect
                your interests, whether that means accepting, countering or waiting for a stronger offer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-10">
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-5">
            {faqs.map((faq, i) => (
              <details key={i} className="group border border-background-200/70 rounded-lg">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-body text-foreground-800 text-sm md:text-base font-medium">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-foreground-600 font-body text-sm leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-foreground-950">
        <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <h2 className="font-heading text-accent-100 text-2xl md:text-3xl mb-4">
            Ready to Take the Next Step?
          </h2>
          <p className="text-background-100/70 font-body text-base mb-6">
            Whether you are ready to list or just starting to explore your options, Carol is here to help.
          </p>
          <a
            href="https://tinyurl.com/carolsellersguide"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-accent-300 font-body text-sm font-semibold hover:text-accent-100 transition-colors mb-8"
          >
            <i className="ri-book-open-line text-lg" />
            Download The Guide to Selling Your Home
            <i className="ri-arrow-right-up-line" />
          </a>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/home-valuation" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
              <i className="ri-home-4-line text-lg" />Request a Free Home Valuation
            </Link>
            <Link to="/contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-accent-300 text-accent-100 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-accent-300/10 transition-all">
              <i className="ri-chat-3-line text-lg" />Contact Carol
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}